import React from 'react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';

/**
 * BulkActionBar — contextual bar that appears when rows are selected (LAT-10).
 *
 * Identical chrome on every list (Customers, Quotes, Jobs line items, etc.)
 * Sticks to the bottom of its scroll container by default; pass
 * `position="top"` to anchor it above the table instead.
 *
 * Pattern at the call site:
 *   const [selected, setSelected] = useState([]);
 *   …
 *   <BulkActionBar
 *     count={selected.length}
 *     onClear={() => setSelected([])}
 *     actions={[
 *       { label: 'Archive', onClick: handleArchive },
 *       { label: 'Export…', onClick: handleExport },
 *       { label: 'Delete', onClick: handleDelete, variant: 'danger' },
 *       // PT-19 CLEANUP-7 (2026-07-24): `variant: 'primary'` renders as a solid-red
 *       // primary pill on the dark chrome (JOB-1 §171 Create Work Order treatment).
 *       // Use for the strong-primary action; `danger` remains red-text destructive.
 *       //
 *       // PR-64 §1.1 (379_ §1): actions can be `disabled` and carry a `title`
 *       // hover-tooltip explaining WHY. A disabled control with no explanation
 *       // is the same defect a lying enabled control is — the user learns
 *       // nothing until after the click. Always pair `disabled` with `title`.
 *       { label: 'Convert to Job', onClick: handleConvert,
 *         disabled: !eligible, title: eligible ? undefined : 'Select a single active quote.' },
 *     ]}
 *   />
 *
 * Row checkbox usage MUST stop propagation so click-checkbox != navigate-row.
 * See <BulkRowCheckbox/> below for a drop-in.
 */
export default function BulkActionBar({
  count = 0,
  onClear,
  actions = [],
  position = 'bottom', // 'bottom' | 'top' | 'static'
  className,
}) {
  if (!count) return null;

  const positional =
    position === 'bottom'
      ? 'fixed bottom-4 left-1/2 -translate-x-1/2'
      : position === 'top'
      ? 'sticky top-4 mx-auto'
      : '';

  return (
    <div
      role="region"
      aria-label={`${count} item${count === 1 ? '' : 's'} selected`}
      className={clsx(
        'z-30 flex items-center gap-3 px-4 py-2 shadow-lg w-fit max-w-full',
        positional,
        className
      )}
      style={{
        backgroundColor: 'var(--color-chrome)',
        color: 'var(--white)',
        borderRadius: 'var(--radius-full)',
      }}
    >
      <span
        className="text-sm font-semibold whitespace-nowrap"
        style={{ color: 'var(--white)' }}
      >
        {count} selected
      </span>
      <span
        className="h-5 w-px"
        style={{ backgroundColor: 'color-mix(in srgb, var(--white) 25%, transparent)' }}
        aria-hidden="true"
      />
      <div className="flex items-center gap-1 flex-wrap">
        {actions.map((a, i) => (
          <button
            key={i}
            type="button"
            onClick={a.onClick}
            disabled={a.disabled}
            title={a.title}
            className={clsx(
              'px-3 py-1 text-sm font-medium rounded-full transition-colors',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50',
              // PT-19 CLEANUP-7 (2026-07-24): `variant='primary'` renders solid-red primary
              // on the dark chrome per JOB-1 §171 (Create Work Order pill). `variant='danger'`
              // is the existing red-text destructive treatment (retain). Default = white text.
              a.variant === 'primary'
                ? 'text-white bg-[color:var(--color-error)] hover:bg-[color:var(--color-error)]/90'
                : a.variant === 'danger'
                  ? 'text-[color:var(--color-error)] hover:bg-white'
                  : 'text-white hover:bg-white/10',
              a.disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            {a.label}
          </button>
        ))}
      </div>
      {onClear && (
        <button
          type="button"
          onClick={onClear}
          className="ml-1 inline-flex items-center justify-center h-6 w-6 rounded-full hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
          aria-label="Clear selection"
        >
          <XMarkIcon className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

/**
 * Drop-in checkbox for selectable rows. Stops propagation so clicking the
 * checkbox doesn't trigger row navigation.
 */
export function BulkRowCheckbox({ checked, onChange, label, className, ...rest }) {
  return (
    <label
      className={clsx(
        'inline-flex items-center justify-center cursor-pointer p-1 -m-1',
        className
      )}
      onClick={(e) => e.stopPropagation()}
    >
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => {
          e.stopPropagation();
          onChange?.(e.target.checked, e);
        }}
        className="h-4 w-4 rounded border-[color:var(--color-border)] text-[color:var(--color-action-primary)] focus:ring-[color:var(--color-action-primary)] focus:ring-offset-0"
        style={{ accentColor: 'var(--color-action-primary)' }}
        aria-label={label || 'Select row'}
        {...rest}
      />
    </label>
  );
}
